// src/pos-app/stations/KdsStation.js
//
// F12a Task 9.1 — the native Aqana POS app's KDS (kitchen display) board.
// The last of the three stations, and deliberately DIFFERENT from its
// siblings (CashierStation / WaiterStation): a KDS is an UNMANNED wall
// display, so there is no operator session, no PIN lock, no Lock button and
// no bottom tabs — the device boots straight to the ticket board. Visual
// reference: docs/mockups/f12a/kds-station.html (glass ONLY on the top bar;
// the board itself is the solid, high-contrast surface of the reused page).
//
// Board: the EXISTING, fully-working OrderViewPage (timers, Start→Bump,
// recall, voids — 1,258 lines) is reused as-is, not rewritten. That page
// reads its queue from the `:category` route param (useParams()), where the
// param is a tenant CATEGORY NAME — the web FunctionsPage links to
// /functions/orders/<cat.name> and the backend filters
// OrderTracking.category == category exactly ('Kitchen' / 'Bar' in the DB,
// see backend/app/routers/orders.py get_orders_by_category). Rendered bare,
// the param would be undefined — so this station hosts a station-local
// <Routes> declaring that exact path (PosAppShell mounts as a direct child
// of the app Router, outside any web <Routes>, so the absolute path is
// legal) and imperatively keeps the URL in sync with the configured queue.
//
// Queue routing + escape interception are ONE rule: the only legitimate
// pathname is the current queue's board path (config.kdsQueue 'kitchen' |
// 'bar' → '/functions/orders/Kitchen' | '/functions/orders/Bar'). A single
// effect navigates (replace:true) whenever the location differs — which
// covers (a) the initial mount (App boots at '/'), (b) queue changes (the
// manager-PIN-gated settings sheet calls saveStation; the new config flows
// back via useStation and re-navigates), and (c) OrderViewPage's
// web-navigation escapes: its header back button navigate('/functions') and
// its no-tenant guard <Navigate to="/functions"/> (unreachable after
// enrollment, which binds the tenant). Queue switches remount the page via
// the route param — correct and intentional (a fresh board for a different
// queue; no cross-queue state worth preserving). Zero changes to the page.
//
// No AuthContext identity concerns: no sales originate from a KDS and there
// is no operator, so setAuthenticatedState is never touched here.
//
// Online pill: now backed by Phase 10's shared ConnectionStatus provider
// (navigator.onLine + online/offline events PLUS a periodic backend health
// ping — see ConnectionStatus.js) instead of the pragmatic navigator-only v1
// this station started with. Same pill UI/CSS; text gains a third state
// ('offline' — no network at all — vs 'reconnecting' — network is up but the
// backend health ping is failing, e.g. a cold start).
//
// Aurora: calm and effectively invisible — the opaque board fills everything
// below the chrome, so the aurora only glows through the glass top bar.
// Keep-awake is global (initNativeChrome); orientation is landscape like
// CashierStation (a wall board is a landscape surface).
//
// Business-day gate (F12a redirect-loop fix): OrderViewPage carries
// useBusinessDayGuard (src/hooks/pos/useBusinessDayGuard.js), which
// navigate('/functions')s whenever there is no active business day —
// isBusinessDay defaults to false, so an unmanned KDS wall display would
// hit this on every boot outside opening hours. The routing effect below
// then reverted that navigate straight back onto OrderViewPage, which
// re-fired the guard: an infinite loop. useBusinessDayGate() reads the same
// state and, while closed, this station simply never mounts OrderViewPage —
// the board pane shows DayClosedScreen instead, so the guard has nothing to
// fire from. The station chip / online pill / settings stay live so the
// device remains configurable while idle.
//
// No customer-facing queue toggle (2026-07-20 decision): the top bar used to
// carry a tappable Kitchen|Bar segmented control next to the station chip.
// A wall-mounted KDS must not let anyone flip its queue with one tap — the
// queue is a device-provisioning setting, same class as the station/Backend
// toggle already removed from the settings sheet — so it is now ONLY
// changeable via the manager-PIN-gated gear (StationSettingsSheet's KDS
// queue segmented control). The station chip (`stationLabel` below) stays as
// a read-only identity indicator showing the current queue.
//
// Surface flag (F12a-N Task K1): SURFACES.kds in surfaces/config.js picks
// what the board pane hosts — 'native' mounts the purpose-built
// <BoardSurface/> (surfaces/kds/), 'legacy' keeps the wrapped OrderViewPage
// plus ALL of the local-<Routes>/param plumbing above. Mirrors
// CashierStation/WaiterStation's switch: the legacy machinery lives in two
// legacy-only components (<LegacyQueueSync/> holds the routing effect — and
// with it the useLocation subscription — <LegacyBoardRoutes/> holds the
// station-local <Routes>), neither of which is registered in native mode.
// The native surface takes the tenant category as a PROP (the same
// QUEUE_CATEGORY mapping, no useParams) and never navigates, so there is
// nothing to sync or intercept; key={category} preserves the legacy
// remount-per-queue-switch semantics. The business-day gate stays ABOVE
// both surfaces (BoardSurface carries no useBusinessDayGuard — while the
// day is closed it simply isn't mounted), and the top bar / station chip /
// online pill / settings are shared chrome, identical in both modes.
// Flipping back to 'legacy' fully restores the wrapper behavior.
import { createPortal } from 'react-dom';
import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import AuroraBackground from '../shell/AuroraBackground';
import StationTopBar from '../shell/StationTopBar';
import { isShortViewport } from '../surfaces/kds/lib/boardFlow';
import StationSettingsSheet from '../StationSettingsSheet';
import ClockPanel from '../ClockPanel';
import DayClosedScreen from '../shell/DayClosedScreen';
import useBusinessDayGate from '../shell/useBusinessDayGate';
import OrderViewPage from '../../pages/pos/OrderViewPage';
import { SURFACES } from '../surfaces/config';
import BoardSurface from '../surfaces/kds/BoardSurface';
import { useStation } from '../../station/StationContext';
import { setOrientation } from '../../platform/nativeChrome';
import { useLanguage } from '../../context/LanguageContext';
import { useConnection } from '../ConnectionStatus';

// config.kdsQueue → the tenant category name OrderViewPage/the backend expect.
//
// `kdsQueue` now holds a CATEGORY NAME chosen from the tenant's own
// kitchen-display categories ("Grill", "PIRI'S GRILLED COLLECTION"), matching
// the web Functions page, which has always listed one KDS per category.
// Devices enrolled before that change still hold the legacy lowercase
// 'kitchen'/'bar', so those two are translated to the capitalised category
// names they always meant; anything else is already a category name and is
// used verbatim.
//
// Getting this wrong is silent and total: mapping an unrecognised queue to
// 'Kitchen' pointed a board at a category the tenant may not have (piri_flame
// has none), and the screen simply showed no orders.
const LEGACY_QUEUE_CATEGORY = { kitchen: 'Kitchen', bar: 'Bar' };

const queueCategoryFor = (kdsQueue) => {
    if (!kdsQueue) return LEGACY_QUEUE_CATEGORY.kitchen;
    return LEGACY_QUEUE_CATEGORY[kdsQueue] || kdsQueue;
};

// Only the two legacy queues are translation keys. A category name is tenant
// data and must be shown verbatim, or the chip renders a raw key.
const isLegacyQueue = (kdsQueue) => Object.prototype.hasOwnProperty.call(
    LEGACY_QUEUE_CATEGORY, kdsQueue || '',
);

const CSS = `
.kst-frame{flex:1;min-height:0;display:flex;flex-direction:column;gap:8px;box-sizing:border-box;
  padding:8px 10px calc(8px + var(--safe-bottom, 0px));}

/* Board — the real OrderViewPage in a solid, clipped viewport (no glass). */
.kst-board{flex:1;min-height:0;position:relative;overflow:hidden;
  border-radius:16px;border:1px solid var(--c-border);background:var(--c-bg);}
.kst-board-inner{position:absolute;inset:0;display:flex;flex-direction:column;}

/* Online / Reconnecting status pill (top-bar center). */
.kst-clock-btn{border:1px solid var(--c-border);background:var(--c-surface);color:var(--c-ink);
  border-radius:999px;padding:5px 13px;font-family:inherit;font-size:11.5px;font-weight:800;cursor:pointer;}
.kst-clock-scrim{position:fixed;inset:0;z-index:2100;display:flex;align-items:center;justify-content:center;
  background:rgba(2,8,23,.6);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);}
.kst-clock-panel{position:relative;display:flex;}
.kst-clock-close{position:absolute;inset-block-start:-6px;inset-inline-end:-6px;z-index:1;
  width:34px;height:34px;border-radius:50%;cursor:pointer;border:1px solid var(--c-border);
  background:var(--c-surface);color:var(--c-muted);font-size:15px;font-weight:800;}
.kst-pill{display:inline-flex;align-items:center;gap:7px;flex:none;
  min-height:32px;padding:0 13px;border-radius:999px;font-size:12px;font-weight:800;
  border:1px solid var(--c-border);background:var(--c-surface);white-space:nowrap;}
.kst-pill .kst-dot{width:8px;height:8px;border-radius:50%;flex:none;}
.kst-pill.kst-on{color:var(--c-success);}
.kst-pill.kst-on .kst-dot{background:var(--c-success);
  box-shadow:0 0 6px color-mix(in srgb, var(--c-success) 80%, transparent);}
.kst-pill.kst-off{color:var(--c-amber);}
.kst-pill.kst-off .kst-dot{background:var(--c-amber);animation:kstBlink 1s ease-in-out infinite;}
@keyframes kstBlink{50%{opacity:.35;}}
@media (prefers-reduced-motion: reduce){.kst-pill .kst-dot{animation:none !important;}}
`;

// ---- legacy-only machinery (SURFACES.kds === 'legacy') -----------------
// Single routing rule: the current queue's board path is the ONLY
// legitimate location (covers mount, queue changes, and OrderViewPage's
// navigate('/functions') escapes — see header). Skipped while there is no
// active business day: OrderViewPage isn't mounted then (see render below),
// so there is no escape to intercept and nothing to sync the route to.
// Lives in its own null-rendering child (mirroring WaiterStation's
// LegacyNavigationGuard) so the effect — and the useLocation subscription —
// simply is not registered when the native surface is active.
function LegacyQueueSync({ dayOpen, queuePath }) {
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (!dayOpen) return;
        if (location.pathname === queuePath) return;
        navigate(queuePath, { replace: true });
    }, [dayOpen, location.pathname, queuePath, navigate]);
    return null;
}

// The station-local <Routes> hosting the wrapped legacy page (see header:
// PosAppShell mounts outside any web <Routes>, so the absolute path is
// legal). Native mode never mounts this — BoardSurface takes `category` as
// a prop instead of reading the :category route param.
function LegacyBoardRoutes() {
    return (
        <Routes>
            <Route path="/functions/orders/:category" element={<OrderViewPage />} />
            {/* Transient non-board locations (pre-sync mount tick) render nothing. */}
            <Route path="*" element={null} />
        </Routes>
    );
}

export default function KdsStation() {
    const { t } = useLanguage();
    const { config } = useStation();
    const { online, reconnecting } = useConnection();
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [clockOpen, setClockOpen] = useState(false);
    const { dayOpen, loading: dayLoading, refresh: refreshDay } = useBusinessDayGate();

    const queueCategory = queueCategoryFor(config?.kdsQueue);
    const queuePath = `/functions/orders/${encodeURIComponent(queueCategory)}`;

    // Board flavour — build-time constant (surfaces/config.js).
    const nativeBoard = SURFACES.kds === 'native';

    // ---- viewport height: drives the compact landscape chrome -----------
    // A phone in landscape has ~393px to spend. The station bar alone is ~90px
    // of it, and with the board's own tabs and banners the tickets were left
    // ~150px — unusable, which is what Creamio's kitchen hit. In that shape the
    // bar is hidden and the board keeps its tabs; Settings and Clock In/Out are
    // a rotation away, the tickets are the job.
    const [viewportHeight, setViewportHeight] = useState(
        () => (typeof window !== 'undefined' ? window.innerHeight : 768));
    useEffect(() => {
        const onResize = () => setViewportHeight(window.innerHeight);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const compactChrome = isShortViewport(viewportHeight);

    // ---- orientation: never locked (2026-09-22) -------------------------
    // This used to lock the board to landscape "for the wall". It no longer
    // locks anything, and joins the other three stations in releasing any lock
    // a device is still carrying from an older build.
    //
    // The 2026-08-27 sweep removed the native lock from Cashier, Waiter and
    // Waiter Sales and concluded "all three POS stations now agree: do not
    // lock" — three, not four. KDS was missed, and kept both native defects
    // that sweep existed to kill:
    //
    //   1. The content inset from capacitor.config.ts's ios.contentInset=
    //      'always' does NOT survive a native lock, so station chrome renders
    //      back under the Dynamic Island — the exact defect F15 part 2 fixed.
    //      Measured directly: WaiterSalesStation reproduced the overlap with a
    //      copy of this lock and lost it the moment the lock was removed.
    //   2. On iPadOS 26 a native lock can force the app into a floating window
    //      where hit-testing desyncs from what is painted — taps landing on
    //      nothing on a perfectly healthy DOM.
    //
    // Reported 2026-09-22: the board would not rotate on a phone. The product
    // requirement the lock served is met without it — a wall-mounted tablet is
    // physically landscape, and iOS honours the app's supported orientations.
    // Chrome under the Island is not a fair price for enforcing in software
    // what a wall bracket already enforces in metal.
    useEffect(() => {
        setOrientation(null);
    }, []);

    return (
        <AuroraBackground intensity="calm">
            <style>{CSS}</style>
            {!nativeBoard && <LegacyQueueSync dayOpen={dayOpen} queuePath={queuePath} />}

            <div className="kst-frame">
                {!compactChrome && <StationTopBar
                    stationLabel={`${isLegacyQueue(config?.kdsQueue) ? t(config.kdsQueue) : queueCategory} · ${t('kds')}`}
                    onSettings={() => setSettingsOpen(true)}
                >
                    <span
                        className={`kst-pill ${online && !reconnecting ? 'kst-on' : 'kst-off'}`}
                        role="status"
                        aria-live="polite"
                    >
                        <span className="kst-dot" aria-hidden="true" />
                        {online ? (reconnecting ? t('reconnecting') : t('online')) : t('offline')}
                    </span>
                    {/* Clock in/out for kitchen staff (client request
                        2026-08-26). A top-bar action opening an overlay, NOT a
                        bottom tab bar: a KDS is an unmanned wall display and
                        the board wants every pixel — adding a permanent tab
                        strip would cost ticket rows on every shift to serve a
                        twice-a-day action. */}
                    <button
                        type="button"
                        className="kst-clock-btn"
                        data-testid="kds-clock-open"
                        onClick={() => setClockOpen(true)}
                    >
                        {t('clock_in_out')}
                    </button>
                </StationTopBar>}

                <main className="kst-board">
                    <div className="kst-board-inner">
                        {dayOpen ? (
                            nativeBoard ? (
                                // key={category}: a queue switch REMOUNTS a fresh
                                // board, same semantics as the legacy route-param
                                // remount (no cross-queue state worth preserving).
                                <BoardSurface
                                    key={queueCategory}
                                    category={queueCategory}
                                />
                            ) : (
                                <LegacyBoardRoutes />
                            )
                        ) : (
                            <DayClosedScreen onRefresh={refreshDay} loading={dayLoading} />
                        )}
                    </div>
                </main>
            </div>

            {/* Portaled: this scrim is position:fixed and the station wraps its
                panes in glass chrome, which would otherwise become its
                containing block. See pos-app/fixedOverlayPortals.test.js. */}
            {clockOpen && createPortal(
                <div className="kst-clock-scrim" data-testid="kds-clock-overlay">
                    <div className="kst-clock-panel">
                        <button
                            type="button"
                            className="kst-clock-close"
                            aria-label={t('close')}
                            onClick={() => setClockOpen(false)}
                        >&#10005;</button>
                        <ClockPanel />
                    </div>
                </div>,
                document.body
            )}

            <StationSettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </AuroraBackground>
    );
}
